import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";
import { Button } from "rizzui";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import { FiltersProvider } from "../../../components/providers/FiltersProvider";
import PageHeader from "../../../layout/shared/page-header";
import { menuRoutes } from "../../../router/menu";
import { UserService } from "../../../services/user/userService";
import cn from "../../../utils/classNames";
import { decodeQueryString } from "../../../utils/transformUrl";
import ContractsList from "./ContractList";

interface ContractsListWrapperProps {
    mode?: 'default' | 'blocked';
}

const ContractsListWrapper: React.FC<ContractsListWrapperProps> = ({ mode = 'default' }) => {

    const { userCan } = useContext(PrivilegeContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [blockedProductStatuses, setBlockedProductStatuses] = useState<string[]>([]);
    const [isLoadingBlockedConfig, setIsLoadingBlockedConfig] = useState<boolean>(mode === 'blocked');

    useEffect(() => {
        if (mode !== 'blocked') {
            setIsLoadingBlockedConfig(false);
            return;
        }
        setIsLoadingBlockedConfig(true);
        (new UserService()).getCompanyConfigurationInfo().then((response) => {
            const data = response.getResponseData()?.data;
            setBlockedProductStatuses(data?.blockedContractProductStatuses ?? []);
        }).catch(() => {
            setBlockedProductStatuses([]);
        }).finally(() => {
            setIsLoadingBlockedConfig(false);
        });
    }, [mode]);

    const isBlockedMode = mode === 'blocked';
    const hasBlockedStatusesConfigured = blockedProductStatuses.length > 0;

    const breadCrumb = isBlockedMode
        ? [
            { name: 'Ventas' },
            { name: 'Gestión de incidencias' },
            { name: 'Bloqueados', href: menuRoutes.incidences.path + menuRoutes.incidences.blockedContracts },
        ]
        : [
            { name: 'Ventas' },
            { name: 'Gestión de contratos' },
            { name: 'Contratos', href: menuRoutes.contracts.path },
        ];

    const blockedStatusesFilter = hasBlockedStatusesConfigured
        ? blockedProductStatuses
        : ['__NO_BLOCKED_STATUS_CONFIGURED__'];

    const hasUrlFilters = useMemo(() => {
        const decoded = decodeQueryString(location.search);
        return !!(decoded?.filter_filters && Object.keys(decoded.filter_filters).length > 0);
    }, [location.search]);

    const defaultFilterFilters = useMemo(() => {
        if (isBlockedMode) {
            return {
                company_active: true,
                organising_corporation_active: true,
                current_product_status: blockedStatusesFilter,
            };
        }

        return {
            company_active: true,
            organising_corporation_active: true,
            ...((!hasUrlFilters && location?.state?.contractStatusId && location?.state?.contractStatusEntity) && {
                custom_status: {
                    current: 1,
                    endDate: null,
                    startDate: null,
                    entity: location.state.contractStatusEntity,
                    statuses: [location.state.contractStatusId],
                }
            })
        };
    }, [isBlockedMode, blockedStatusesFilter, hasUrlFilters, location?.state?.contractStatusId, location?.state?.contractStatusEntity]);

    return (
        <Fragment>
            <FiltersProvider defaultFilterFilters={defaultFilterFilters}>
                <PageHeader title={isBlockedMode ? "Bloqueados" : "Listado de contratos"} breadcrumb={breadCrumb}>
                    {userCan('create_contracts', 'contracts') && !isBlockedMode && (
                        <Button
                            color="primary"
                            onClick={() => { navigate(menuRoutes.contracts.create) }}
                            className={cn(['mt-4 md:mt-0'])}
                        >
                            Crear contrato
                        </Button>
                    )}
                </PageHeader>

                {isBlockedMode && isLoadingBlockedConfig && (
                    <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                        Cargando configuración de estados bloqueados...
                    </div>
                )}

                {isBlockedMode && !isLoadingBlockedConfig && !hasBlockedStatusesConfigured && (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <div className="flex items-center gap-2 font-medium">
                            <FaExclamationTriangle className="h-4 w-4" />
                            No se han configurado estados para el listado Bloqueados.
                        </div>
                        {userCan('admin_contracts', 'contracts') && (
                            <Button
                                size="sm"
                                variant="outline"
                                color="primary"
                                className="mt-3"
                                onClick={() => navigate(menuRoutes.settings.path + menuRoutes.settings.contracts)}
                            >
                                Ir a configuración de contratos
                            </Button>
                        )}
                    </div>
                )}

                {(!isBlockedMode || !isLoadingBlockedConfig) && (
                    <ContractsList
                        mode={mode}
                        forcedCurrentProductStatuses={blockedStatusesFilter}
                        showStatusFilter={!isBlockedMode}
                    />
                )}
            </FiltersProvider>
        </Fragment>
    );
}

export default ContractsListWrapper;
